import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import useLocalStorage from '../hooks/useLocalStorage';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';

const ToastContext = createContext(null);

const MAX_NOTIFICATIONS = 200;

export function ToastProvider({ children }) {
  const { username, role, isReady, isAuthenticated } = useAuth();
  const [toasts, setToasts] = useState([]);
  const [notifications, setNotifications] = useLocalStorage('zlux-notifications', []);
  // Server-side notifications - things that happened to this account from
  // *another* device or another person's action (e.g. staff resolving a
  // support ticket), which the old localStorage-only history could never
  // show since it never left the browser that triggered it.
  const [serverNotifications, setServerNotifications] = useState([]);

  useEffect(() => {
    if (!isReady || !isAuthenticated) {
      setServerNotifications([]);
      return undefined;
    }
    let cancelled = false;
    api.get('/notifications')
      .then(({ notifications: fetched }) => {
        if (cancelled) return;
        setServerNotifications(fetched.map((item) => ({
          id: `server-${item.id}`,
          message: item.message,
          type: item.type,
          timestamp: new Date(item.createdAt).getTime(),
          read: item.read,
          link: item.link,
          username: username || 'Unknown',
          role: role || 'user',
          server: true,
        })));
      })
      .catch(() => {
        if (!cancelled) setServerNotifications([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isReady, isAuthenticated, username, role]);

  const addToast = (message, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { id, message, type }]);
    window.setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, 2600);

    setNotifications((current) => [
      { id, message, type, timestamp: Date.now(), read: false, username: username || 'Unknown', role: role || 'user' },
      ...current,
    ].slice(0, MAX_NOTIFICATIONS));
  };

  const markAllRead = () => {
    setNotifications((current) => (current.some((item) => !item.read) ? current.map((item) => ({ ...item, read: true })) : current));
    setServerNotifications((current) => (current.some((item) => !item.read) ? current.map((item) => ({ ...item, read: true })) : current));
    if (isAuthenticated) {
      api.post('/notifications/read-all').catch(() => {});
    }
  };

  const allNotifications = useMemo(
    () => [...serverNotifications, ...notifications].sort((a, b) => b.timestamp - a.timestamp),
    [serverNotifications, notifications],
  );

  const unreadCount = allNotifications.filter((item) => !item.read).length;

  return (
    <ToastContext.Provider value={{ addToast, notifications: allNotifications, unreadCount, markAllRead }}>
      <div className="toast-stack">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast-${toast.type}`}>
            {toast.message}
          </div>
        ))}
      </div>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
